
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const codeSchema = z.object({
  code: z.string().length(4, "Code must be 4 digits"),
});

export type CodeFormValues = z.infer<typeof codeSchema>;

interface CodeVerificationFormProps {
  onSubmit: (values: CodeFormValues) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

export const CodeVerificationForm = ({ onSubmit, onBack, isLoading }: CodeVerificationFormProps) => {
  const form = useForm<CodeFormValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: {
      code: "",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem className="space-y-6">
              <FormLabel>Verification Code</FormLabel>
              <FormControl>
                <InputOTP 
                  maxLength={4} 
                  value={field.value}
                  onChange={field.onChange}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            "Verify & Login"
          )}
        </Button>

        <Button 
          variant="ghost" 
          type="button" 
          className="w-full mt-2" 
          onClick={onBack}
          disabled={isLoading}
        >
          Go Back
        </Button>
      </form>
    </Form>
  );
};
